// pages/secure/MyOfficePage.js
// The logged-in user's own virtual office: door (presence), license wall, bio,
// photo, and public contact details.

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { useAuth } from '../../hooks/useAuth';

// The managing broker's own site searches (My Site Searches): new by-right finds at a glance.
const SiteSearchCard = () => {
    const apiClient = useApiClient();
    const [s, setS] = useState(null);
    useEffect(() => {
        let live = true;
        apiClient.get('/api/broker-site-searches/summary').then((r) => { if (live) setS(r); }).catch(() => {});
        return () => { live = false; };
    }, [apiClient]);
    return (
        <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
            <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">My Site Searches</p>
                {s ? (
                    <p className="text-sm text-slate-700">
                        {s.active} active search{s.active === 1 ? '' : 'es'}
                        {' · '}<strong className={s.new_by_right ? 'text-green-700' : ''}>{s.new_by_right} new by-right find{s.new_by_right === 1 ? '' : 's'}</strong>
                        {s.new_finds > s.new_by_right ? ` (${s.new_finds} new in all)` : ''}
                        {' · '}last sweep {s.last_swept_at ? new Date(s.last_swept_at).toLocaleString() : 'not yet'}
                    </p>
                ) : <p className="text-sm text-slate-500">Space where your uses are allowed by right, swept daily, by town and address.</p>}
            </div>
            <Link to="/dashboard/my-site-searches" className="rounded bg-slate-800 px-3 py-1.5 text-sm font-semibold text-white">Open</Link>
        </div>
    );
};

const MyOfficePage = () => {
    const apiClient = useApiClient();
    const { user } = useAuth();
    const showSiteSearches = !!user && user.role !== 'examiner' && !!(user.isManagingBroker || user.role === 'admin');
    const [office, setOffice] = useState(null);
    const [form, setForm] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [savedFlash, setSavedFlash] = useState(false);

    const load = useCallback(() => {
        setIsLoading(true);
        apiClient.get('/api/office/me')
            .then(data => {
                setOffice(data);
                setForm({
                    display_title: data.display_title || '',
                    bio: data.bio || '',
                    public_phone: data.public_phone || '',
                    public_email: data.public_email || '',
                    show_license_wall: data.show_license_wall !== 0,
                    door_open: data.door_open !== 0,
                    status_message: data.status_message || '',
                });
            })
            .catch(err => setError(err.message || 'Failed to load your office.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError('');
        try {
            const updated = await apiClient.put('/api/office/me', form);
            setOffice(updated);
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 2500);
        } catch (err) {
            setError(err.message || 'Failed to save your office.');
        } finally {
            setIsSaving(false);
        }
    };

    const handlePhoto = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('photo', file);
        try {
            await apiClient.post('/api/office/me/photo', formData, true);
            load();
        } catch (err) {
            setError(err.message || 'Failed to upload photo.');
        }
    };

    if (isLoading) return <p className="p-6">Opening your office...</p>;
    if (!office || !form) return <p className="p-6 text-red-500">{error || 'Office not found.'}</p>;

    const licenseExpired = office.license_expiration_date && new Date(office.license_expiration_date) < new Date();

    return (
        <div className="p-6 max-w-5xl">
            <h1 className="text-2xl font-bold text-slate-800 mb-1">My Office</h1>
            <p className="text-slate-500 mb-6">This is your room in the building — what colleagues see when they stop by your door.</p>

            {showSiteSearches && <SiteSearchCard />}

            {error && <p className="mb-4 text-red-500">{error}</p>}
            {savedFlash && <p className="mb-4 text-green-600">✓ Office updated.</p>}

            <div className="grid md:grid-cols-2 gap-6">
                {/* The wall — what's displayed */}
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center gap-4 mb-4">
                        {office.photo_path
                            ? <img src={`/office-media/${office.photo_path}`} alt="" className="w-20 h-20 rounded-full object-cover border" />
                            : <div className="w-20 h-20 rounded-full bg-slate-200 flex items-center justify-center text-2xl font-bold text-slate-500">
                                {(office.name || office.username || '?').charAt(0)}
                              </div>}
                        <div>
                            <p className="text-xl font-semibold text-slate-800">{office.name || office.username}</p>
                            <p className="text-slate-500">{office.display_title || (office.staff_role || (office.agent_id ? 'Licensed Broker' : ''))}</p>
                            <label className="text-sm text-blue-600 cursor-pointer hover:underline">
                                Change photo
                                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhoto} className="hidden" />
                            </label>
                        </div>
                    </div>

                    {office.agent_id && (
                        <div className={`border rounded-md p-4 mb-4 ${licenseExpired ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
                            <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Illinois Real Estate License — on the wall</p>
                            <p className="font-mono text-lg text-slate-800">{office.license_number || '—'}</p>
                            <p className={`text-sm ${licenseExpired ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>
                                {office.license_expiration_date
                                    ? `${licenseExpired ? 'EXPIRED' : 'Expires'} ${new Date(office.license_expiration_date).toLocaleDateString()}`
                                    : 'No expiration on file'}
                            </p>
                        </div>
                    )}

                    <div className={`flex items-center gap-2 text-sm font-medium ${form.door_open ? 'text-green-600' : 'text-slate-500'}`}>
                        <span className={`inline-block w-3 h-3 rounded-full ${form.door_open ? 'bg-green-500' : 'bg-slate-400'}`}></span>
                        {form.door_open ? 'Door open — in the office' : 'Door closed — away'}
                        {form.status_message && <span className="text-slate-500 font-normal">· {form.status_message}</span>}
                    </div>
                </div>

                {/* The controls */}
                <form onSubmit={handleSave} className="bg-white rounded-lg shadow p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Title on the door</label>
                        <input name="display_title" value={form.display_title} onChange={handleChange} placeholder="e.g. Broker Associate"
                               className="mt-1 w-full border rounded-md px-3 py-2" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700">About me (on the wall)</label>
                        <textarea name="bio" value={form.bio} onChange={handleChange} rows="4"
                                  className="mt-1 w-full border rounded-md px-3 py-2" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Public phone</label>
                            <input name="public_phone" value={form.public_phone} onChange={handleChange}
                                   className="mt-1 w-full border rounded-md px-3 py-2" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700">Public email</label>
                            <input name="public_email" type="email" value={form.public_email} onChange={handleChange}
                                   className="mt-1 w-full border rounded-md px-3 py-2" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700">Status message</label>
                        <input name="status_message" value={form.status_message} onChange={handleChange} placeholder="e.g. In a closing until 3pm"
                               className="mt-1 w-full border rounded-md px-3 py-2" />
                    </div>
                    <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input type="checkbox" name="door_open" checked={form.door_open} onChange={handleChange} />
                            Door open (I'm in)
                        </label>
                        {office.agent_id && (
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                                <input type="checkbox" name="show_license_wall" checked={form.show_license_wall} onChange={handleChange} />
                                Display license on my wall
                            </label>
                        )}
                    </div>
                    <button type="submit" disabled={isSaving}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                        {isSaving ? 'Saving...' : 'Save my office'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default MyOfficePage;
